import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Video, Search, ChevronLeft, ChevronRight, Clock, Play, CheckCircle, BookOpen, X, Square, CheckSquare, ArrowRight, Folder, FolderOpen, KeyRound, AlertTriangle, ChevronDown, ChevronUp, Plus, GraduationCap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { PageHeader } from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_CATEGORIES = [
  { id: 'programming',  label_en: 'Programming',       label_tr: 'Programlama' },
  { id: 'design',       label_en: 'Design',             label_tr: 'Tasarım' },
  { id: 'business',     label_en: 'Business',           label_tr: 'İş Dünyası' },
  { id: 'marketing',    label_en: 'Marketing',          label_tr: 'Pazarlama' },
  { id: 'music',        label_en: 'Music',              label_tr: 'Müzik' },
  { id: 'photography',  label_en: 'Photography',        label_tr: 'Fotoğraf' },
  { id: 'language',     label_en: 'Languages',          label_tr: 'Diller' },
  { id: 'health',       label_en: 'Health',             label_tr: 'Sağlık' },
  { id: 'cabin_crew',   label_en: 'Cabin Crew',         label_tr: 'Kabin Memurluğu' },
  { id: 'hr_training',  label_en: 'HR Training',        label_tr: 'İnsan Kaynakları' },
];

export default function AdminVimeo() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);

  // === Klasorden kurs olusturma modali ===
  const [createCourseFolder, setCreateCourseFolder] = useState(null);
  const [createForm, setCreateForm] = useState({ title: '', description: '', category: 'hr_training', level: 'beginner' });
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [_page, setPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(false);

  // Accounts
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);

  // Multi-select
  const [selected, setSelected] = useState([]);
  const [showAssign, setShowAssign] = useState(false);

  // Assign
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  const [createLessonsMode, setCreateLessonsMode] = useState(true);
  // ===== Token Probe (debug) =====
  const [probeOpen, setProbeOpen] = useState(false);
  const [probeToken, setProbeToken] = useState("");
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [probeError, setProbeError] = useState(null);
  const [showRawFolders, setShowRawFolders] = useState(false);

  const runProbe = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!probeToken.trim()) return;
    setProbeLoading(true);
    setProbeResult(null);
    setProbeError(null);
    const tokenSnapshot = probeToken.trim();
    try {
      const res = await axios.post(`${API}/admin/vimeo/probe`, { token: tokenSnapshot }, { headers: authHeaders() });
      setProbeResult(res.data);
    } catch (err) {
      setProbeError((err && err.response && err.response.data && err.response.data.detail) || (err && err.message) || "Unknown error");
    } finally {
      setProbeToken("");
      setProbeLoading(false);
    }
  };


  useEffect(() => {
    // Fetch accounts first
    axios.get(`${API}/admin/vimeo/accounts`, { headers: authHeaders() })
      .then(res => {
        const accs = res.data.accounts || [];
        setAccounts(accs);
        if (accs.length > 0) {
          setActiveAccount(accs[0]);
          fetchFolders(accs[0].id);
        } else {
          setLoading(false);
        }
      }).catch(() => setLoading(false));
    axios.get(`${API}/courses?limit=200`).then(r => setCourses(r.data.courses || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFolders = (accountId) => {
    setLoading(true);
    axios.get(`${API}/admin/vimeo/folders?account=${accountId}`, { headers: authHeaders() })
      .then(res => { setFolders(res.data.folders || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const switchAccount = (acc) => {
    setActiveAccount(acc);
    setSelectedFolder(null);
    setVideos([]);
    setSelected([]);
    setSearch('');
    setSearchInput('');
    fetchFolders(acc.id);
  };

  const openFolder = async (folder) => {
    setSelectedFolder(folder);
    setSelected([]);
    setPage(1);
    setSearch('');
    setSearchInput('');
    fetchFolderVideos(folder.folder_id, 1, '');
  };

  const fetchFolderVideos = async (folderId, pg, q) => {
    setLoadingVideos(true);
    try {
      let url = `${API}/admin/vimeo/folders/${folderId}/videos?page=${pg}&per_page=100&account=${activeAccount?.id || ''}`;
      if (q) url += `&search=${encodeURIComponent(q)}`;
      const res = await axios.get(url, { headers: authHeaders() });
      setVideos(res.data.videos || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { setVideos([]); }
    finally { setLoadingVideos(false); }
  };

  const searchAllVideos = async () => {
    if (!searchInput) return;
    setSelectedFolder(null);
    setSelected([]);
    setLoadingVideos(true);
    try {
      const res = await axios.get(`${API}/admin/vimeo/videos?page=1&per_page=100&search=${encodeURIComponent(searchInput)}&account=${activeAccount?.id || ''}`, { headers: authHeaders() });
      setVideos(res.data.videos || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setSearch(searchInput);
    } catch { setVideos([]); }
    finally { setLoadingVideos(false); }
  };

  const toggleSelect = (video) => { setSelected(prev => prev.some(v => v.vimeo_id === video.vimeo_id) ? prev.filter(v => v.vimeo_id !== video.vimeo_id) : [...prev, video]); };
  const selectAll = () => { selected.length === videos.length ? setSelected([]) : setSelected([...videos]); };
  const isSelected = (id) => selected.some(v => v.vimeo_id === id);
  const formatDuration = (sec) => { const m = Math.floor(sec / 60); const s = sec % 60; return `${m}:${s.toString().padStart(2, '0')}`; };

  const openAssignModal = () => { setShowAssign(true); setSelectedCourse(null); setLessons([]); setAssignResult(null); setCreateLessonsMode(true); };

  // Assign entire folder
  const assignFolder = () => { setSelected([...videos]); openAssignModal(); };

  const selectCourse = async (course) => {
    setSelectedCourse(course);
    setLoadingLessons(true);
    try {
      const res = await axios.get(`${API}/courses/${course.course_id}`);
      setLessons((res.data.lessons || []).sort((a, b) => a.order - b.order));
    } catch { setLessons([]); }
    finally { setLoadingLessons(false); }
  };

  const assignToExistingLessons = async () => {
    if (!selectedCourse) return;
    setAssigning(true);
    let count = 0;
    const empty = lessons.filter(l => !l.vimeo_url);
    for (let i = 0; i < Math.min(selected.length, empty.length); i++) {
      try {
        await axios.post(`${API}/admin/vimeo/assign`, { lesson_id: empty[i].lesson_id, vimeo_url: selected[i].embed_url, vimeo_name: selected[i].name }, { headers: authHeaders() });
        count++;
      } catch { /* tek video ataması başarısız — döngü devam eder, count artırılmaz */ }
    }
    setAssignResult({ count, total: selected.length }); setAssigning(false);
    selectCourse(selectedCourse);
  };

  const createLessonsFromVideos = async () => {
    if (!selectedCourse) return;
    setAssigning(true);
    let count = 0;
    const startOrder = lessons.length + 1;
    for (let i = 0; i < selected.length; i++) {
      try {
        await axios.post(`${API}/instructor/lessons`, {
          course_id: selectedCourse.course_id,
          title: selected[i].name.replace(/\.\w+$/, '').trim(),
          title_tr: selected[i].name.replace(/\.\w+$/, '').trim(),
          order: startOrder + i, lesson_type: 'video',
          vimeo_url: selected[i].embed_url,
          duration_minutes: Math.ceil(selected[i].duration / 60),
          description: selected[i].description || ''
        }, { headers: authHeaders() });
        count++;
      } catch { /* ders oluşturma başarısız — döngü devam eder */ }
    }
    setAssignResult({ count, total: selected.length, created: true }); setAssigning(false);
    selectCourse(selectedCourse);
  };

  // === Klasorden kurs olustur ===
  const openCreateCourseModal = (folder) => {
    setCreateCourseFolder(folder);
    setCreateForm({
      title: folder.name + ' Eğitimi',
      description: '',
      category: 'hr_training',
      level: 'beginner',
    });
    setCreateError(null);
  };
  const closeCreateCourseModal = () => {
    setCreateCourseFolder(null);
    setCreateError(null);
  };
  const submitCreateCourse = async () => {
    if (!createCourseFolder || !createForm.title.trim()) return;
    setCreatingCourse(true);
    setCreateError(null);
    try {
      const res = await axios.post(
        API + '/admin/vimeo/import-folder-as-course',
        {
          account_id: activeAccount && activeAccount.id,
          folder_id: createCourseFolder.folder_id,
          course: {
            title: createForm.title.trim(),
            description: createForm.description.trim() || undefined,
            category: createForm.category,
            level: createForm.level,
          },
          module_title: createCourseFolder.name,
        },
        { headers: authHeaders() }
      );
      const courseId = res && res.data && res.data.course_id;
      if (courseId) {
        navigate('/instructor/courses/' + courseId + '/edit');
      } else {
        setCreateCourseFolder(null);
      }
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      const msg = (detail && typeof detail === 'object') ? (detail.message_tr || detail.message_en || JSON.stringify(detail)) : (detail || (err && err.message) || 'Hata');
      setCreateError(msg);
    } finally {
      setCreatingCourse(false);
    }
  };

  const CreateCourseModal = () => {
    if (!createCourseFolder) return null;
    const folderVideoCount = createCourseFolder.video_count != null ? createCourseFolder.video_count : (createCourseFolder.videos_count != null ? createCourseFolder.videos_count : 0);
    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeCreateCourseModal}
          data-testid="create-course-modal-overlay">
          <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
            className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="create-course-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-display text-lg font-bold flex items-center gap-2">
                  <GraduationCap size={20} className="text-emerald-500" />
                  {lang === 'tr' ? 'Klasörden Kurs Oluştur' : 'Create Course from Folder'}
                </h3>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="font-semibold">{createCourseFolder.name}</span>
                  {' - '}
                  {folderVideoCount} {lang === 'tr' ? 'video' : 'videos'}
                </p>
              </div>
              <button onClick={closeCreateCourseModal} disabled={creatingCourse} className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold mb-1">{lang === 'tr' ? 'Kurs Başlığı' : 'Course Title'} *</label>
                <input type="text" value={createForm.title}
                  onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-emerald-500 outline-none text-sm"
                  data-testid="create-course-title-input" />
              </div>

              <div>
                <label className="block text-xs font-bold mb-1">{lang === 'tr' ? 'Açıklama (opsiyonel)' : 'Description (optional)'}</label>
                <textarea value={createForm.description}
                  onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                  rows={3}
                  placeholder={lang === 'tr' ? 'Kurs hakkında kısa bir açıklama...' : 'Short description about the course...'}
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-emerald-500 outline-none text-sm resize-none"
                  data-testid="create-course-desc-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1">{lang === 'tr' ? 'Kategori' : 'Category'}</label>
                  <select value={createForm.category}
                    onChange={e => setCreateForm({ ...createForm, category: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-emerald-500 outline-none text-sm bg-white"
                    data-testid="create-course-category-select">
                    {ALL_CATEGORIES.map(c => (
                      <option key={c.id} value={c.id}>{lang === 'tr' ? c.label_tr : c.label_en}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">{lang === 'tr' ? 'Seviye' : 'Level'}</label>
                  <select value={createForm.level}
                    onChange={e => setCreateForm({ ...createForm, level: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-emerald-500 outline-none text-sm bg-white"
                    data-testid="create-course-level-select">
                    <option value="beginner">{lang === 'tr' ? 'Başlangıç' : 'Beginner'}</option>
                    <option value="intermediate">{lang === 'tr' ? 'Orta' : 'Intermediate'}</option>
                    <option value="advanced">{lang === 'tr' ? 'İleri' : 'Advanced'}</option>
                  </select>
                </div>
              </div>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 text-xs text-emerald-800">
                <p className="font-bold flex items-center gap-1 mb-1"><CheckCircle size={12} /> {lang === 'tr' ? 'Otomatik Yapılacaklar' : 'Auto Actions'}</p>
                <ul className="list-disc list-inside space-y-0.5 text-[11px]">
                  <li>{lang === 'tr' ? 'Klasördeki tüm videolar ders olarak eklenir' : 'All videos in folder added as lessons'}</li>
                  <li>{lang === 'tr' ? 'Klasör adı ile bir modül oluşturulur' : 'A module is created with folder name'}</li>
                  <li>{lang === 'tr' ? 'Kurs taslak olarak kaydedilir (yayınlanmaz)' : 'Course saved as draft (not published)'}</li>
                </ul>
              </div>

              {createError && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid="create-course-error">
                  <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 break-all">{typeof createError === 'string' ? createError : JSON.stringify(createError)}</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={closeCreateCourseModal} disabled={creatingCourse}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold text-[var(--text-muted)] hover:bg-gray-50 disabled:opacity-50"
                  data-testid="create-course-cancel">
                  {lang === 'tr' ? 'İptal' : 'Cancel'}
                </button>
                <button type="button" onClick={submitCreateCourse} disabled={creatingCourse || !createForm.title.trim()}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="create-course-submit">
                  {creatingCourse ? (lang === 'tr' ? 'Oluşturuluyor...' : 'Creating...') : (
                    <>
                      <Plus size={14} />
                      {lang === 'tr' ? 'Kursu Oluştur' : 'Create Course'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // ============ FOLDERS VIEW ============
  if (!selectedFolder && !search) {
    return (
      <AdminLayout title="Vimeo Library" titleTr={t('admin.vimeo_page_title')}>
        <PageHeader
          title={t('admin.vimeo_page_title')}
          subtitle={t('admin.vimeo_page_subtitle')}
          breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.vimeo_page_title') }]}
        />

        {/* ===== Token Probe (yalnız geliştirme ortamında göster) ===== */}
        {process.env.NODE_ENV === 'development' && <div className="mb-5 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/50 overflow-hidden">
          <button
            type="button"
            onClick={() => setProbeOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-100/40 transition-colors"
            data-testid="probe-toggle"
          >
            <span className="flex items-center gap-2 font-bold text-sm text-amber-900">
              <KeyRound size={16} className="text-amber-600" />
              Token Test (debug) - manuel API key ile videolari getir
            </span>
            {probeOpen ? <ChevronUp size={16} className="text-amber-700" /> : <ChevronDown size={16} className="text-amber-700" />}
          </button>

          {probeOpen && (
            <div className="px-4 pb-4 pt-1 border-t border-amber-200">
              <p className="text-[11px] text-amber-800 mb-3">
                Vimeo Personal Access Token yapistir. Token sadece bu istek icin kullanilir; kaydedilmez, loglanmaz.
              </p>
              <form onSubmit={runProbe} className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="password"
                  value={probeToken}
                  onChange={(e) => setProbeToken(e.target.value)}
                  placeholder="vimeo_pat_..."
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1 px-3 py-2 rounded-xl border-2 border-amber-300 bg-white font-mono text-xs focus:border-amber-500 focus:outline-none"
                  data-testid="probe-token-input"
                />
                <button
                  type="submit"
                  disabled={probeLoading || !probeToken.trim()}
                  className="px-5 py-2 rounded-xl bg-amber-500 text-white font-bold text-xs hover:bg-amber-600 disabled:opacity-50 transition-colors"
                  data-testid="probe-submit"
                >
                  {probeLoading ? "Test ediliyor..." : "Test et ve videolari getir"}
                </button>
              </form>

              {probeError && (
                <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-red-50 border-2 border-red-200" data-testid="probe-error">
                  <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-bold text-red-700 mb-1">Hata</p>
                    <pre className="whitespace-pre-wrap break-all text-red-600 font-mono text-[11px]">{typeof probeError === "string" ? probeError : JSON.stringify(probeError, null, 2)}</pre>
                  </div>
                </div>
              )}

              {probeResult && (
                <div className="mt-3 space-y-3" data-testid="probe-result">
                  <div className={`p-3 rounded-xl border-2 ${probeResult.account && probeResult.account.valid ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                    {probeResult.account && probeResult.account.valid ? (
                      <div className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 text-xs">
                          <p className="font-bold text-green-700">Token gecerli</p>
                          <p className="text-green-800 mt-0.5">
                            <span className="font-semibold">{probeResult.account.name}</span>
                            {probeResult.account.plan && <span className="ml-2 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-bold">{probeResult.account.plan}</span>}
                          </p>
                          <p className="text-green-700 mt-1 font-mono text-[11px]">
                            videos: {probeResult.account.total_videos} | folders: {probeResult.account.total_folders}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <X size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="text-xs"><p className="font-bold text-red-700">Token gecersiz veya /me yaniti alinamadi</p></div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-xl bg-white border-2 border-amber-200">
                      <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Raw (tum klasorler)</p>
                      <p className="text-2xl font-bold text-amber-900 mt-1">{probeResult.folders_raw_count}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-white border-2 border-sky-200">
                      <p className="text-[10px] font-bold text-sky-700 uppercase tracking-wide">Filtreli (videos &gt; 0)</p>
                      <p className="text-2xl font-bold text-sky-900 mt-1">{probeResult.folders_filtered_count}</p>
                    </div>
                  </div>

                  {probeResult.folders_raw && probeResult.folders_raw.length > 0 && (
                    <div className="rounded-xl bg-white border-2 border-amber-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowRawFolders(v => !v)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-amber-50 transition-colors"
                        data-testid="probe-raw-toggle"
                      >
                        <span className="text-xs font-bold text-amber-800">Klasor listesi (raw)</span>
                        {showRawFolders ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {showRawFolders && (
                        <div className="max-h-64 overflow-y-auto border-t border-amber-200">
                          <table className="w-full text-[11px] font-mono">
                            <thead className="bg-amber-50 sticky top-0">
                              <tr className="text-left">
                                <th className="px-3 py-1.5 font-bold text-amber-800">Name</th>
                                <th className="px-3 py-1.5 font-bold text-amber-800 text-right">videos.total</th>
                                <th className="px-3 py-1.5 font-bold text-amber-800 text-right">items.total</th>
                                <th className="px-3 py-1.5 font-bold text-amber-800">folder_id</th>
                              </tr>
                            </thead>
                            <tbody>
                              {probeResult.folders_raw.map((f) => (
                                <tr key={f.folder_id || f.name} className={`border-t border-amber-100 ${f.videos_total === 0 ? "bg-red-50/40 text-red-700" : ""}`}>
                                  <td className="px-3 py-1">{f.name || "(unnamed)"}</td>
                                  <td className="px-3 py-1 text-right font-bold">{f.videos_total}</td>
                                  <td className="px-3 py-1 text-right">{f.items_total}</td>
                                  <td className="px-3 py-1 text-[var(--text-muted)]">{f.folder_id}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {probeResult.videos && probeResult.videos.length > 0 && (
                    <div className="rounded-xl bg-white border-2 border-sky-200 overflow-hidden">
                      <p className="px-3 py-2 text-xs font-bold text-sky-800 bg-sky-50 border-b border-sky-200">Son 10 video preview</p>
                      <div className="divide-y divide-sky-100 max-h-72 overflow-y-auto">
                        {probeResult.videos.map((v) => (
                          <div key={v.vimeo_id} className="flex items-center gap-2 px-3 py-2">
                            <div className="w-16 h-10 rounded bg-gray-900 flex-shrink-0 overflow-hidden">
                              {v.thumbnail ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Video size={12} className="text-gray-600" /></div>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-xs truncate">{v.name}</p>
                              <p className="text-[10px] text-[var(--text-muted)] font-mono">{v.vimeo_id} | {Math.floor(v.duration / 60)}:{(v.duration % 60).toString().padStart(2, "0")}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {probeResult.errors && probeResult.errors.length > 0 && (
                    <div className="p-3 rounded-xl bg-red-50 border-2 border-red-200" data-testid="probe-errors">
                      <p className="text-xs font-bold text-red-700 mb-1 flex items-center gap-1">
                        <AlertTriangle size={12} /> Hatalar ({probeResult.errors.length})
                      </p>
                      <ul className="space-y-1 text-[11px] font-mono text-red-700">
                        {probeResult.errors.map((er, i) => (
                          <li key={i}>[{er.step}] {er.status} - {typeof er.error === "string" ? er.error : JSON.stringify(er.error)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>}

        {/* Account Tabs */}

        {accounts.length > 1 && (
          <div className="flex gap-2 mb-5">
            {accounts.map(acc => (
              <button key={acc.id} onClick={() => switchAccount(acc)}
                className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${activeAccount?.id === acc.id ? 'bg-sky-500 text-white shadow-lg' : 'bg-white text-[var(--text-muted)] border-2 border-[var(--border-light)] hover:border-sky-300'}`}
                data-testid={`account-${acc.id}`}>
                <Video size={16} />
                {acc.name}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${activeAccount?.id === acc.id ? 'bg-white/20' : 'bg-gray-100'}`}>{acc.video_count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Search across all */}
        <form onSubmit={(e) => { e.preventDefault(); searchAllVideos(); }} className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={lang === 'tr' ? 'Tüm videolarda ara...' : 'Search all videos...'} data-testid="vimeo-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" />
          </div>
          <button type="submit" className="btn-primary px-6 py-2.5 text-sm">{lang === 'tr' ? 'Ara' : 'Search'}</button>
        </form>

        {loading ? (
          <div className="card-clay p-8 text-center"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : (
          <div>
            <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Folder size={20} className="text-sky-500" /> {lang === 'tr' ? 'Klasörler' : 'Folders'} ({folders.length})</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {folders.map((f, i) => {
                const vc = f.video_count ?? f.videos_count ?? 0;
                const isEmpty = vc === 0;
                return (
                  <motion.div key={f.folder_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className={`card-clay p-5 text-left transition-all group flex flex-col gap-3 ${isEmpty ? 'opacity-70 hover:opacity-100 hover:border-amber-300' : 'hover:border-sky-400'}`} data-testid={`folder-${i}`}>
                    <button type="button" onClick={() => openFolder(f)} className="flex items-center gap-3 text-left w-full">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${isEmpty ? 'bg-gray-100 group-hover:bg-amber-50' : 'bg-sky-50 group-hover:bg-sky-100'}`}>
                        <FolderOpen size={24} className={isEmpty ? 'text-gray-400' : 'text-sky-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-sm truncate ${isEmpty ? 'text-[var(--text-muted)]' : ''}`}>{f.name}</p>
                        <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${isEmpty ? 'bg-gray-100 text-gray-500' : 'bg-sky-50 text-sky-700'}`}>
                          {vc} {lang === 'tr' ? 'video' : 'video'}{isEmpty ? (lang === 'tr' ? ' (boş)' : ' (empty)') : ''}
                        </span>
                      </div>
                      <ChevronRight size={16} className={`group-hover:text-sky-500 ${isEmpty ? 'text-gray-300' : 'text-[var(--text-muted)]'}`} />
                    </button>
                    {!isEmpty && (
                      <button type="button" onClick={() => openCreateCourseModal(f)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-sm"
                        data-testid={`create-course-btn-${i}`}>
                        <GraduationCap size={14} />
                        {lang === 'tr' ? 'Bu Klasörden Kurs Oluştur' : 'Create Course from Folder'}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      <CreateCourseModal />
      </AdminLayout>
    );
  }

  // ============ VIDEOS LIST (inside folder or search) ============
  return (
    <AdminLayout title="Vimeo Library" titleTr={t('admin.vimeo_page_title')}>
      {/* Back + Folder name */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => { setSelectedFolder(null); setSearch(''); setSearchInput(''); setVideos([]); setSelected([]); }}
          className="flex items-center gap-1 text-sm font-bold text-[var(--brand-primary)] hover:underline" data-testid="back-to-folders">
          <ChevronLeft size={16} /> {lang === 'tr' ? 'Klasörler' : 'Folders'}
        </button>
        <span className="text-[var(--text-muted)]">/</span>
        <div className="flex items-center gap-2">
          <FolderOpen size={18} className="text-sky-500" />
          <span className="font-bold text-sm">{selectedFolder?.name || (lang === 'tr' ? 'Arama Sonuçları' : 'Search Results')}</span>
          <span className="text-xs text-[var(--text-muted)]">({total} video)</span>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {selected.length > 0 && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-2">
            <span className="text-sm font-bold text-[var(--brand-primary)]">{selected.length} {lang === 'tr' ? 'secili' : 'selected'}</span>
            <button onClick={openAssignModal} className="btn-success px-4 py-2 text-sm flex items-center gap-1" data-testid="bulk-assign-btn">
              <ArrowRight size={14} /> {lang === 'tr' ? 'Eğitim Ata' : 'Assign'}
            </button>
            <button onClick={() => setSelected([])} className="btn-tactile px-3 py-2 text-sm bg-white border-[var(--border-light)]"><X size={14} /></button>
          </motion.div>
        )}
        {videos.length > 0 && selected.length === 0 && (
          <button onClick={assignFolder} className="btn-primary px-4 py-2 text-sm flex items-center gap-1" data-testid="assign-folder-btn">
            <FolderOpen size={14} /> {lang === 'tr' ? 'Tüm Klasörü Eğitim Ata' : 'Assign Entire Folder'}
          </button>
        )}
      </div>

      {/* Select All + Video List */}
      {loadingVideos ? (
        <div className="card-clay p-8 text-center"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : videos.length > 0 ? (
        <>
          <div className="flex items-center gap-3 mb-3">
            <button onClick={selectAll} className="flex items-center gap-2 text-sm font-bold text-[var(--text-muted)] hover:text-[var(--text-main)]" data-testid="select-all-btn">
              {selected.length === videos.length ? <CheckSquare size={18} className="text-[var(--brand-primary)]" /> : <Square size={18} />}
              {lang === 'tr' ? 'Tümünü Seç' : 'Select All'} ({videos.length})
            </button>
          </div>
          <div className="space-y-1.5 mb-6">
            {videos.map((v, i) => {
              const sel = isSelected(v.vimeo_id);
              return (
                <div key={v.vimeo_id} onClick={() => toggleSelect(v)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${sel ? 'border-[var(--brand-primary)] bg-[#E8F5FD]' : 'border-[var(--border-light)] bg-white hover:border-gray-300'}`}
                  data-testid={`vimeo-row-${i}`}>
                  <div className="flex-shrink-0">{sel ? <CheckSquare size={20} className="text-[var(--brand-primary)]" /> : <Square size={20} className="text-[var(--text-muted)]" />}</div>
                  <div className="w-20 h-12 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0">
                    {v.thumbnail ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Video size={16} className="text-gray-600" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{v.name}</p>
                    {v.description && <p className="text-[10px] text-[var(--text-muted)] truncate">{v.description}</p>}
                  </div>
                  <span className="text-xs font-bold text-[var(--text-muted)] flex items-center gap-1 flex-shrink-0"><Clock size={12} /> {formatDuration(v.duration)}</span>
                  <a href={v.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-[var(--text-muted)] flex-shrink-0"><Play size={14} /></a>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="card-clay p-8 text-center"><Video size={36} className="mx-auto text-[var(--text-muted)] mb-3" /><p className="font-bold text-sm text-[var(--text-muted)]">{lang === 'tr' ? 'Bu klasörde video yok' : 'No videos in this folder'}</p></div>
      )}

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssign && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAssign(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="assign-modal">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-display text-lg font-bold">{lang === 'tr' ? 'Toplu Video Atama' : 'Bulk Assign'}</h3>
                  <p className="text-xs text-[var(--text-muted)]">{selected.length} video{selectedFolder ? ` - ${selectedFolder.name}` : ''}</p>
                </div>
                <button onClick={() => setShowAssign(false)} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
              </div>

              {/* Selected preview */}
              <div className="bg-gray-50 rounded-xl p-3 mb-4 max-h-28 overflow-y-auto">
                {selected.slice(0, 10).map((v, i) => (
                  <div key={v.vimeo_id} className="flex items-center gap-2 py-0.5 text-xs">
                    <span className="font-bold text-[var(--text-muted)] w-5">{i + 1}.</span>
                    <span className="font-semibold truncate flex-1">{v.name}</span>
                    <span className="text-[var(--text-muted)]">{formatDuration(v.duration)}</span>
                  </div>
                ))}
                {selected.length > 10 && <p className="text-[10px] text-[var(--text-muted)] pt-1">...+{selected.length - 10} {lang === 'tr' ? 'daha' : 'more'}</p>}
              </div>

              {/* Result */}
              {assignResult && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4 text-center" data-testid="assign-result">
                  <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
                  <p className="font-bold text-sm text-green-700">{assignResult.count}/{assignResult.total} {assignResult.created ? (lang === 'tr' ? 'ders oluşturuldu!' : 'lessons created!') : (lang === 'tr' ? 'video atandı!' : 'assigned!')}</p>
                  <button onClick={() => { setShowAssign(false); setSelected([]); setAssignResult(null); }} className="btn-primary px-6 py-2 text-sm mt-3">{lang === 'tr' ? 'Tamam' : 'Done'}</button>
                </div>
              )}

              {/* Step 1: Course */}
              {!assignResult && !selectedCourse && (
                <div>
                  <p className="text-xs font-bold text-[var(--text-muted)] mb-2">{lang === 'tr' ? '1. Kurs Seçin' : '1. Select Course'}</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {courses.map(c => (
                      <button key={c.course_id} onClick={() => selectCourse(c)} className="w-full text-left p-3 rounded-xl border-2 border-[var(--border-light)] hover:border-sky-400 flex items-center gap-3">
                        <BookOpen size={16} className="text-sky-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0"><p className="font-bold text-xs truncate">{c.title_tr || c.title}</p><p className="text-[10px] text-[var(--text-muted)]">{c.category}</p></div>
                        <ChevronRight size={14} className="text-[var(--text-muted)]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Method */}
              {!assignResult && selectedCourse && (
                <div>
                  <button onClick={() => { setSelectedCourse(null); setLessons([]); }} className="text-xs font-bold text-[var(--brand-primary)] flex items-center gap-1 mb-3 hover:underline">
                    <ChevronLeft size={12} /> {selectedCourse.title_tr || selectedCourse.title}
                  </button>
                  <p className="text-xs font-bold text-[var(--text-muted)] mb-3">{lang === 'tr' ? '2. Yöntem Seçin' : '2. Choose Method'}</p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button onClick={() => setCreateLessonsMode(true)} className={`p-4 rounded-xl border-2 text-left ${createLessonsMode ? 'border-green-400 bg-green-50' : 'border-[var(--border-light)]'}`} data-testid="create-lessons-option">
                      <p className="font-bold text-sm mb-1">{lang === 'tr' ? 'Yeni Dersler Oluştur' : 'Create Lessons'}</p>
                      <p className="text-[10px] text-[var(--text-muted)]">{selected.length} {lang === 'tr' ? 'ders oluşturulur' : 'lessons created'}</p>
                    </button>
                    <button onClick={() => setCreateLessonsMode(false)} className={`p-4 rounded-xl border-2 text-left ${!createLessonsMode ? 'border-sky-400 bg-sky-50' : 'border-[var(--border-light)]'}`} data-testid="assign-existing-option">
                      <p className="font-bold text-sm mb-1">{lang === 'tr' ? 'Mevcut Derslere Ata' : 'To Existing'}</p>
                      <p className="text-[10px] text-[var(--text-muted)]">{lessons.filter(l => !l.vimeo_url).length} {lang === 'tr' ? 'boş ders' : 'empty lessons'}</p>
                    </button>
                  </div>

                  {loadingLessons ? (
                    <div className="p-4 text-center"><div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                  ) : createLessonsMode ? (
                    <div>
                      <div className="bg-green-50 rounded-xl p-3 max-h-36 overflow-y-auto space-y-0.5">
                        {selected.map((v, i) => (
                          <div key={v.vimeo_id} className="flex items-center gap-2 text-xs py-0.5">
                            <span className="font-bold text-green-600 w-5">{lessons.length + i + 1}.</span>
                            <span className="font-semibold truncate flex-1">{v.name.replace(/\.\w+$/, '')}</span>
                            <span className="text-[var(--text-muted)]">{Math.ceil(v.duration / 60)} dk</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={createLessonsFromVideos} disabled={assigning} className="btn-success w-full py-3 mt-3 disabled:opacity-50" data-testid="create-lessons-btn">
                        {assigning ? '...' : `${selected.length} ${lang === 'tr' ? 'Ders Oluştur' : 'Create Lessons'}`}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="bg-sky-50 rounded-xl p-3 max-h-36 overflow-y-auto space-y-1">
                        {lessons.filter(l => !l.vimeo_url).slice(0, selected.length).map((l, i) => (
                          <div key={l.lesson_id} className="flex items-center gap-2 text-xs py-0.5">
                            <span className="font-bold w-4">{l.order}.</span>
                            <span className="font-semibold truncate flex-1 text-sky-700">{l.title_tr || l.title}</span>
                            <ArrowRight size={10} className="text-[var(--text-muted)]" />
                            <span className="truncate max-w-[120px] text-[var(--text-muted)]">{selected[i]?.name}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={assignToExistingLessons} disabled={assigning || lessons.filter(l => !l.vimeo_url).length === 0} className="btn-primary w-full py-3 mt-3 disabled:opacity-50" data-testid="assign-existing-btn">
                        {assigning ? '...' : `${Math.min(selected.length, lessons.filter(l => !l.vimeo_url).length)} ${lang === 'tr' ? 'Derse Ata' : 'Assign'}`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}
